import moment from 'moment'
import i18n from '@/i18n'
import { t } from 'i18next'
import { useMatch, useMatchTeam } from './Match'
import { EventFormat, Match, Team } from '@/types'
import { useGetPlayerProfile } from '@/services'

export const displayDate = (date: moment.Moment, fmt: string) => {
  const isToday = date.isSame(moment(), 'days')
  const isYesterday = date.isSame(moment().subtract(1, 'days'), 'days')
  return isToday ? t('common.today') : isYesterday ? t('common.yesterday') : date.format(fmt)
}

const DATE_FORMATS: Record<string, string> = {
  en: 'MM/DD/YYYY',
  zh_hans: 'YYYY/MM/DD',
  zh_tw: 'YYYY/MM/DD',
}

export function formatMatchDateByLang(dateLike: moment.MomentInput, lang: string = i18n.language): string {
  if (!dateLike) return ''
  const key = lang.toLowerCase().replace('-', '_')
  const fmt = DATE_FORMATS[key] ?? 'DD/MM/YYYY'
  return displayDate(moment(dateLike), fmt)
}

export type Valence = 'positive' | 'negative' | 'neutral'

export enum MatchStatus {
  INITIALIZATION = 'Initialization',
  PENDING = 'Pending Validation',
  CALCULATING = 'Calculating Ratings',
  READY = 'Ready',
}

export function calculateMatchStatus(match: Match): MatchStatus {
  if (!match.confirmed) return MatchStatus.PENDING
  if (!match.eloCalculated && moment(match.created).isAfter(moment('2023-06-06T06:25:54')))
    return MatchStatus.CALCULATING
  if (match.initialization) return MatchStatus.INITIALIZATION
  return MatchStatus.READY
}

function calculateValence(won: boolean, matchCalculated: boolean, impact: number | null): Valence {
  if (matchCalculated === false) return 'neutral'
  if (impact === 0) {
    return won ? 'positive' : 'negative'
  }
  if (impact && impact > 0) {
    return 'positive'
  } else if (impact && impact < 0) {
    return 'negative'
  }
  return 'neutral'
}

type RatingDisplay = string
type ImpactDisplayValue = string
export type ImpactDirection = 'up' | 'down' | 'noChange'

export type Impact = {
  displayValue: ImpactDisplayValue
  direction: ImpactDirection
  rawValue: number | null
}

function getImpactValues(team: Team, playerSlot: 1 | 2, eventFormat: EventFormat): Impact {
  const eventTypeFormatted: 'Single' | 'Double' = eventFormat === 'DOUBLES' ? 'Double' : 'Single'

  const slot: '1' | '2' = playerSlot === 1 ? '1' : '2'

  const impactRaw = team?.preMatchRatingAndImpact?.[`match${eventTypeFormatted}RatingImpactPlayer${slot}`]

  const impact = ImpactDisplay(impactRaw)

  return {
    displayValue: impact,
    direction: impactRaw > 0 ? 'up' : impactRaw < 0 ? 'down' : 'noChange',
    rawValue: impactRaw,
  }
}

export function useMatchRatingAndImpact(
  player: Match['teams'][0]['player1'] | Match['teams'][0]['player2'],
  playerSlot: number,
  playerProfile: ReturnType<typeof useGetPlayerProfile>
): {
  impact: Impact
  preMatchRating: RatingDisplay
  postMatchRating: RatingDisplay
  valence: Valence
} {
  const { eventFormat, eloCalculated } = useMatch()
  const { team } = useMatchTeam()

  const slot: '1' | '2' = playerSlot === 1 ? '1' : '2'

  const impact = getImpactValues(team, playerSlot as 1 | 2, eventFormat)

  const eventTypePost: 'doubles' | 'singles' = eventFormat === 'DOUBLES' ? 'doubles' : 'singles'

  const preMatchRaw =
    team?.preMatchRatingAndImpact?.[`preMatch${eventFormat === 'DOUBLES' ? 'Double' : 'Single'}RatingPlayer${slot}`]

  const profileRatingRaw = playerProfile.data?.ratings[eventTypePost]

  const preMatchRating = getPreMatchRatingAsString(eloCalculated, preMatchRaw, profileRatingRaw)

  const postMatchRating = calculatePostMatchRating(
    eloCalculated,
    player?.postMatchRating?.[eventTypePost],
    preMatchRating
  )

  const valence = calculateValence(team.winner === true, eloCalculated, impact.rawValue)

  return {
    impact,
    preMatchRating,
    postMatchRating,
    valence,
  }
}

function getPreMatchRatingAsString(
  eloCalculated: boolean,
  preMatchFromMatch: number | null | undefined,
  currentProfileRating: string | undefined
): RatingDisplay {
  if (eloCalculated) {
    // If elo has been calculated and preMatchFromMatch is still null/undefined we can assume they
    // were "NR" before the match. Incalculable matches (forfeit/withdrawal/tie) omit the field
    // entirely, so it arrives as `undefined` — guard both, matching calculatePostMatchRating, or
    // ratingDisplay() would call .toFixed on undefined and crash the card.
    if (preMatchFromMatch === null || preMatchFromMatch === undefined) return 'NR'
    return ratingDisplay(preMatchFromMatch)
  } else {
    // Profile always has a rating, so we can return that
    // If the profile rating is undefined, we return 'NR'
    const profileRating = currentProfileRating || 'NR'
    return profileRating
  }
}

function calculatePostMatchRating(
  eloCalculated: boolean,
  postMatchClaim: number | null | undefined,
  preMatchClaim: RatingDisplay
): RatingDisplay {
  if (eloCalculated) {
    // If elo has been calculated and postMatchClaim is still null
    // we can assume they are still "NR" after the match
    if (postMatchClaim === null || postMatchClaim === undefined) return 'NR'
    return ratingDisplay(postMatchClaim)
  } else {
    // If elo is not calculated, we return the preMatchClaim as postMatchClaim
    return preMatchClaim
  }
}

function ratingDisplay(ratingRaw: number): RatingDisplay {
  return ratingRaw.toFixed(3)
}

export function ImpactDisplay(impact: number): string {
  if (typeof impact !== 'number') {
    return ''
  }
  const impactString = impact > 0 ? `+${impact?.toFixed(3)}` : impact?.toFixed(3)
  return impactString.replace(/0\./, '.')
}

export function hasValidatedMatch<T extends { validatedMatch?: boolean }>(
  p: T | undefined
): p is T & { validatedMatch: boolean } {
  return !!p && 'validatedMatch' in p
}

type PlayerLike = { id: number; validatedMatch?: boolean } | undefined | null

/** Does this data model expose the validated flag at all? */
export function hasValidatedField(p: PlayerLike): boolean {
  return !!p && typeof p === 'object' && 'validatedMatch' in p
}

/** Has this specific player already validated? */
export function isValidated(p: PlayerLike): boolean {
  return !!p?.validatedMatch
}
